import React from "react";
import {
  ThemeProvider,
  CssBaseline,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  AppBar,
  Toolbar,
  Link,
  Card,
  CardContent,
} from "@mui/material";
import { ExpandMore } from "@mui/icons-material";
import { Lock, Search, Grid as GridIcon, Film, Download, Shield } from "react-feather";
import theme from "./theme";
import ImageCarousel from "./components/ImageCarousel";
import "./App.css";

const CWS_URL = "https://chromewebstore.google.com/detail/secretbee/kndpjmmnmfbnpbmajjocagobilhoemio";

function App() {
  // Scrolls an element to just below the fixed AppBar. offsetTop is relative to
  // the nearest positioned ancestor, so it can't be used (or summed) to get a
  // document-relative position for nested sections.
  const scrollToId = (id) => {
    const el = document.getElementById(id);
    if (!el) return;
    const header = document.querySelector("header");
    const headerHeight = header ? header.offsetHeight : 64;
    const top =
      el.getBoundingClientRect().top + window.scrollY - headerHeight - 20;
    window.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
  };

  const scrollToContent = () => scrollToId("content");

  const scrollToSection = (id) => scrollToId(id);

  const carouselImages = [
    "/assets/Screen2.png",
    "/assets/Screen3.png",
    "/assets/Screen4.png",
  ];

  const carouselAltTexts = [
    "SecretBee list view with search",
    "SecretBee popup",
    "SecretBee import and export",
  ];

  const features = [
    {
      icon: <Lock size={32} color="#818cf8" />,
      title: "Password Protected",
      body: "Each password opens its own isolated vault. No accounts, no recovery — by design.",
    },
    {
      icon: <Search size={32} color="#818cf8" />,
      title: "Instant Search",
      body: "Full-text search across title, URL, tags, and description. Filter by tag:keyword.",
    },
    {
      icon: <GridIcon size={32} color="#818cf8" />,
      title: "Grid & List Views",
      body: "Browse with favicons and thumbnails in grid view, or scan quickly in compact list view.",
    },
    {
      icon: <Film size={32} color="#818cf8" />,
      title: "Video Thumbnails",
      body: "YouTube thumbnails are auto-detected and displayed in the grid — no extra steps.",
    },
    {
      icon: <Download size={32} color="#818cf8" />,
      title: "Import & Export",
      body: "Back up your bookmarks to JSON and restore them anytime. Duplicate detection included.",
    },
    {
      icon: <Shield size={32} color="#818cf8" />,
      title: "No Cloud, No Tracking",
      body: "Everything is stored locally in Chrome's storage. Nothing is sent anywhere, ever.",
    },
  ];

  const faqItems = [
    {
      question: "How do I install SecretBee?",
      answer: <>Install it from the <Link href={CWS_URL} target="_blank" rel="noopener noreferrer" sx={{ color: "#818cf8" }}>Chrome Web Store</Link>. You need to be using Google Chrome.</>,
    },
    {
      question: "Is SecretBee free?",
      answer: "Yes. SecretBee is free and contains no advertising.",
    },
    {
      question: "What happens if I forget my password?",
      answer:
        "There is no recovery. Your bookmarks are stored under a hash of your password — if the password is lost, the data cannot be accessed. This is intentional.",
    },
    {
      question: "Where are my bookmarks stored?",
      answer:
        "In Chrome's local storage, on your device. Nothing is transmitted to any server. SecretBee has no backend.",
    },
    {
      question: 'What is the "bee" shortcut?',
      answer:
        'Type "bee" in Chrome\'s address bar followed by a space or tab to activate the omnibox shortcut. You can use it to quickly open your bookmarks manager.',
    },
    {
      question: "Who made SecretBee?",
      answer:
        "SecretBee is made by Sam Ennis, a software engineer from Boston who has made a couple of free focus and privacy tools. It contains no advertising and stores no data from you.",
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* Navigation */}
      <AppBar position="fixed">
        <Toolbar>
          <Box
            component="img"
            src="/assets/Logo.svg"
            alt="SecretBee Logo"
            sx={{ width: 40, height: 40, marginRight: 1.5 }}
          />
          <Typography
            variant="h6"
            component="div"
            sx={{
              flexGrow: 1,
              color: "#818cf8",
              fontWeight: 700,
              fontFamily: '"Quicksand", sans-serif',
            }}
          >
            SecretBee
          </Typography>
          <Link
            component="button"
            variant="body1"
            onClick={() => scrollToSection("faq")}
            sx={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", marginRight: 3, cursor: "pointer", "&:hover": { color: "#818cf8" } }}
          >
            FAQ
          </Link>
          <Link
            component="button"
            variant="body1"
            onClick={() => scrollToSection("about")}
            sx={{ color: "rgba(255,255,255,0.7)", textDecoration: "none", cursor: "pointer", "&:hover": { color: "#818cf8" } }}
          >
            About
          </Link>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          minHeight: "100vh",
          background: "linear-gradient(135deg, #0f0f1a 0%, #1a1040 100%)",
          paddingTop: "48px",
        }}
      >
        {/* Hero */}
        <Container
          maxWidth="md"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            textAlign: "center",
            pt: 8,
            pb: 8,
          }}
        >
          <Box
            component="img"
            src="/assets/Logo.svg"
            alt="SecretBee Logo"
            sx={{
              width: 140,
              height: 140,
              mb: 3,
              filter: "drop-shadow(0 8px 24px rgba(102, 126, 234, 0.5))",
            }}
          />

          <Typography variant="h1" component="h1" gutterBottom>
            Your bookmarks. Private.
          </Typography>

          <Typography
            variant="body1"
            sx={{ color: "rgba(255,255,255,0.55)", fontSize: "1.2rem", mb: 4, maxWidth: 520 }}
          >
            Password-protected bookmark collections stored entirely on your device.
            No accounts. No cloud. No tracking.
          </Typography>

          <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", justifyContent: "center" }}>
            <Button
              variant="contained"
              size="large"
              href={CWS_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              Add to Chrome — it's free
            </Button>
            <Button variant="outlined" size="large" onClick={scrollToContent}>
              Learn more
            </Button>
          </Box>
        </Container>

        {/* Content */}
        <Container maxWidth="lg" id="content" sx={{ pb: 3, minHeight: "100vh", pt: 8 }}>
          <Grid container spacing={4}>

            {/* Carousel */}
            <Grid item xs={12} id="about">
              <ImageCarousel images={carouselImages} altTexts={carouselAltTexts} />
            </Grid>

            {/* Features */}
            <Grid item xs={12}>
              <Typography variant="h2" gutterBottom sx={{ mt: 4, mb: 4 }}>
                What SecretBee Does
              </Typography>
              <Grid container spacing={3}>
                {features.map((f, i) => (
                  <Grid item xs={12} sm={6} md={4} key={i}>
                    <Card sx={{ height: "100%", textAlign: "center", p: 2 }}>
                      <CardContent>
                        <Box sx={{
                          width: 68, height: 68, borderRadius: "50%",
                          background: "rgba(129, 140, 248, 0.12)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          mx: "auto", mb: 2,
                        }}>
                          {f.icon}
                        </Box>
                        <Typography variant="h6" gutterBottom>{f.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{f.body}</Typography>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Grid>

            {/* FAQ */}
            <Grid item xs={12} id="faq">
              <Typography variant="h2" gutterBottom sx={{ mb: 4, mt: 4 }}>
                Frequently Asked
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {faqItems.map((item, index) => (
                  <Accordion key={index}>
                    <AccordionSummary expandIcon={<ExpandMore sx={{ color: "#818cf8" }} />}>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                        <Typography sx={{ color: "#818cf8", fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.08em", flexShrink: 0 }}>
                          Q
                        </Typography>
                        <Typography variant="subtitle1" fontWeight={600}>
                          {item.question}
                        </Typography>
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <Box sx={{ display: "flex", gap: 1.5, alignItems: "baseline" }}>
                        <Typography sx={{ color: "rgba(129,140,248,0.6)", fontWeight: 800, fontSize: "0.8rem", letterSpacing: "0.08em", flexShrink: 0 }}>
                          A
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                          {item.answer}
                        </Typography>
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                ))}
              </Box>
            </Grid>

          </Grid>
        </Container>

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 4, color: "rgba(255,255,255,0.25)", fontSize: "0.85rem" }}>
          SecretBee — made by Sam Ennis
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
